import React, { useMemo, useEffect } from "react";
import { useRouter } from "expo-router";
import { useLiveRoom } from "@hooks/useLiveRoom";
import { useLiveRoomConnections } from "@hooks/useLiveRoomConnections";
import { useLiveRoomActivity } from "@hooks/useLiveRoomActivity";
import { useArtists } from "@hooks/trpc/useArtists";
import LText from "@components/LText";
import LBox from "@components/LBox";
import LPressable from "@components/LPressable";
import LIcon from "@components/LIcon";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { theme } from "@theme";
import { useAuth } from "@utils/firebaseAuth/auth";
import {
  useSharedValue,
  useAnimatedStyle,
  withRepeat,
  withTiming,
  interpolateColor,
  FadeInDown,
} from "react-native-reanimated";
import Animated from "react-native-reanimated";
import LScrollView from "@components/LScrollView";
import { useAccess } from "@hooks/trpc/useAccesses";
import { Platform } from "react-native";

// Animated tile component with pulsating effect during countdown
const AnimatedActivityTile = ({
  children,
  isCountdown,
}: {
  children: React.ReactNode;
  isCountdown: boolean;
}) => {
  const scale = useSharedValue(1);
  const pulse = useSharedValue(0);

  useEffect(() => {
    if (isCountdown) {
      // Start pulsating animation for scale
      scale.value = withRepeat(
        withTiming(1.02, { duration: 800 }),
        -1, // Infinite repeat
        true, // Reverse (oscillate)
      );
      // Start pulsating animation for background color
      pulse.value = withRepeat(
        withTiming(1, { duration: 800 }),
        -1, // Infinite repeat
        true, // Reverse (oscillate)
      );
    } else {
      // Stop animation and reset to normal
      scale.value = withTiming(1, { duration: 200 });
      pulse.value = withTiming(0, { duration: 200 });
    }
  }, [isCountdown, scale, pulse]);

  const animatedStyle = useAnimatedStyle(() => {
    const backgroundColor = interpolateColor(
      pulse.value,
      [0, 1],
      ["rgba(255, 255, 255, 0.1)", "rgba(255, 255, 255, 0.15)"],
    );

    return {
      transform: [{ scale: scale.value }],
      backgroundColor,
      borderRadius: theme.borderRadii["m-l"],
      borderWidth: 0,
    };
  });

  return <Animated.View style={animatedStyle}>{children}</Animated.View>;
};

const LiveRoom = ({ accessId }: { accessId: string }) => {
  const router = useRouter();
  const { liveRoomData, isLoading, isLive } = useLiveRoom(accessId);
  const { firebaseUser } = useAuth();
  // Show activities when room exists (not just when live, so users can see prizes)
  // Show all non-pending activities (pending activities are admin-only pre-configuration)
  const activitiesToShow = liveRoomData
    ? liveRoomData.activities?.filter((a) => a.status !== "pending") || []
    : [];
  const { activities } = useLiveRoomActivity(activitiesToShow);
  const insets = useSafeAreaInsets();

  const access = liveRoomData?.access;

  // Fetch artist data for images
  const artistIds = useMemo(() => {
    if (!access) return [];
    const ids = [access.artistId];
    if (access.collaborators) {
      ids.push(...access.collaborators.map((c: { id: string }) => c.id));
    }
    return ids;
  }, [access]);
  const { query: artistsQuery } = useArtists(artistIds);
  const artists = artistsQuery.data || [];

  const { data: apiAccess } = useAccess(accessId);

  const ticketClaimed =
    apiAccess?.type === "event" &&
    apiAccess?.userTickets?.some((t) => t.claimed);

  const shouldShowLiveRoom = useMemo(() => {
    if (Platform.OS === "web") return false;
    if (isLoading) return false;
    if (!ticketClaimed) return false;
    if (!liveRoomData) return false;
    if (!isLive) return false;
    return true;
  }, [isLoading, liveRoomData, isLive, ticketClaimed]);

  // Connect to live room only when it should be shown
  useLiveRoomConnections(accessId, shouldShowLiveRoom);

  if (!shouldShowLiveRoom) return null;

  return (
    <LBox
      width="100%"
      height="100%"
      position="absolute"
      top={0}
      left={0}
      right={0}
      bottom={0}
      borderRadius="m"
      bg="blackTranslucent90"
      entering={FadeInDown}
    >
      <LScrollView
        contentContainerStyle={{
          backgroundColor: "transparent",
          padding: theme.spacing.s4,
          paddingBottom: insets.bottom + theme.spacing.s3,
          gap: theme.spacing.s3,
          flexGrow: 1,
        }}
      >
        <LBox gap="s4">
          {access && (
            <>
              <LText variant="Body/B1/SemiBold" color="white">
                Live Activities
              </LText>
              {/* Activity Timeline */}
              {activities.length > 0 ? (
                <LBox gap="s3">
                  {activities
                    .sort((a, b) => {
                      const aTime =
                        typeof a.created_at === "string"
                          ? new Date(a.created_at).getTime()
                          : (a.created_at as any)?.toMillis?.() || 0;
                      const bTime =
                        typeof b.created_at === "string"
                          ? new Date(b.created_at).getTime()
                          : (b.created_at as any)?.toMillis?.() || 0;
                      return bTime - aTime; // Most recent first
                    })
                    .map((act) => (
                      <AnimatedActivityTile
                        key={act.id}
                        isCountdown={act.status === "countdown"}
                      >
                        <LPressable
                          sentryEventName="activity_tile_pressed"
                          onPress={() => {
                            router.push(`/access/${accessId}/live/${act.id}`);
                          }}
                        >
                          <LBox
                            paddingVertical="s4"
                            paddingHorizontal="s3"
                            borderRadius="m-l"
                            borderWidth={1}
                            borderColor={
                              act.status === "completed"
                                ? "whiteTranslucent20"
                                : act.status === "active" ||
                                    act.status === "countdown"
                                  ? "white"
                                  : "whiteTranslucent20"
                            }
                            style={{ overflow: "hidden" }}
                            gap="s3"
                          >
                            {/* Countdown overlay - appears as cutout in background */}
                            {act.status === "countdown" && (
                              <LBox
                                style={{
                                  position: "absolute",
                                  right: 60,
                                  top: 0,
                                  bottom: 0,
                                  justifyContent: "center",
                                  alignItems: "center",
                                  zIndex: 0,
                                }}
                              >
                                <LText variant="h1" fontWeight="900">
                                  {act.countdown_remaining ?? 0}
                                </LText>
                              </LBox>
                            )}
                            {/* Win/Loss badge overlay - appears in same location as countdown */}
                            {act.status === "completed" &&
                              act.type !== "choose_the_cover" &&
                              firebaseUser?.uid && (
                                <LBox
                                  style={{
                                    position: "absolute",
                                    right: 60,
                                    top: 0,
                                    bottom: 0,
                                    justifyContent: "center",
                                    alignItems: "center",
                                    zIndex: 0,
                                  }}
                                >
                                  <LBox
                                    borderWidth={1}
                                    borderColor={
                                      act.winners.includes(firebaseUser.uid)
                                        ? "accessCheckoutValidationSuccess"
                                        : "danger"
                                    }
                                    borderRadius="m"
                                    paddingHorizontal="s2"
                                    paddingVertical="s1"
                                  >
                                    <LText
                                      variant="Body/B2/SemiBold"
                                      color={
                                        act.winners.includes(firebaseUser.uid)
                                          ? "accessCheckoutValidationSuccess"
                                          : "danger"
                                      }
                                    >
                                      {act.winners.includes(firebaseUser.uid)
                                        ? "You Won"
                                        : "You Lost"}
                                    </LText>
                                  </LBox>
                                </LBox>
                              )}
                            <LBox
                              flexDirection="row"
                              alignItems="center"
                              justifyContent="space-between"
                              style={{ zIndex: 1 }}
                            >
                              <LBox flex={1} gap="s1">
                                <LBox flexDirection="column" gap="s1">
                                  <LText
                                    variant="Body/B1/Medium"
                                    color={
                                      act.status === "completed"
                                        ? "whiteTranslucent70"
                                        : "white"
                                    }
                                  >
                                    {act.type === "free_drink"
                                      ? "Free Drink Giveaway"
                                      : act.type === "serenade_ticket"
                                        ? "Serenade Ticket"
                                        : "Choose the Cover"}
                                  </LText>
                                  {act.artist_id && (
                                    <LBox
                                      flexDirection="row"
                                      alignItems="center"
                                      gap="s1"
                                    >
                                      <LText
                                        variant="Body/B2/Regular"
                                        color={
                                          act.status === "completed"
                                            ? "whiteTranslucent50"
                                            : "whiteTranslucent70"
                                        }
                                      >
                                        {artists.find(
                                          (a: { id: string; name?: string }) =>
                                            a.id === act.artist_id,
                                        )?.name || "Unknown"}
                                      </LText>
                                    </LBox>
                                  )}
                                </LBox>
                              </LBox>
                              <LIcon
                                colors={{
                                  color:
                                    act.status === "completed"
                                      ? theme.colors.whiteTranslucent50
                                      : "white",
                                }}
                                name="chevron"
                                rotate={270}
                                size={20}
                              />
                            </LBox>
                          </LBox>
                        </LPressable>
                      </AnimatedActivityTile>
                    ))}
                </LBox>
              ) : (
                <LBox gap="s3">
                  <LBox
                    backgroundColor="whiteTranslucent10"
                    padding="s4"
                    borderRadius="m-l"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <LText
                      variant="Body/B2/Regular"
                      color="whiteTranslucent80"
                      textAlign="center"
                    >
                      Activities will be announced here soon.
                    </LText>
                  </LBox>
                </LBox>
              )}

              {/* Rewards Inventory */}
            </>
          )}
        </LBox>
      </LScrollView>
    </LBox>
  );
};

export default LiveRoom;
